import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import { get } from 'lodash'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import useDeploymentsResources from '../../resources/api/v1/admin/docker/useDeploymentsResources'

const generateDefaultName = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  return `D${year}${month}${day}-${hours}${minutes}`
}

function PendingChangesModal({ open, onClose, project, teamSlug }) {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)

  const { create: createDeployment } = useDeploymentsResources(teamSlug, project?.slug)

  const handleDeploy = useCallback(() => {
    if (!project) return

    setLoading(true)
    const deploymentData = {
      deployment: {
        name: generateDefaultName(),
        nodeIds: get(project, 'nodeIds', []),
      },
    }

    createDeployment(deploymentData)
      .then(() => {
        enqueueSnackbar(
          formatMessage({ id: 'navigation.pendingChanges.modal.deploySuccess' }),
          { variant: 'success' }
        )
        onClose()
      })
      .catch(() => {
        enqueueSnackbar(
          formatMessage({ id: 'navigation.pendingChanges.modal.deployError' }),
          { variant: 'error' }
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [project, createDeployment, enqueueSnackbar, formatMessage, onClose])

  const handleGoToProject = useCallback(() => {
    if (!project || !teamSlug) return
    navigate(`/teams/${teamSlug}/docker/projects/${project.slug}`)
    onClose()
  }, [project, teamSlug, navigate, onClose])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <FormattedMessage id="navigation.pendingChanges.modal.title" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage
            id="navigation.pendingChanges.modal.message"
            values={{ projectName: project?.name || '' }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleGoToProject} disabled={loading}>
          <FormattedMessage id="navigation.pendingChanges.modal.goToProjectButton" />
        </Button>
        <Button
          variant="contained"
          onClick={handleDeploy}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            bgcolor: 'pendingChanges.main',
            color: 'pendingChanges.contrastText',
            '&:hover': {
              bgcolor: 'pendingChanges.light',
            },
          }}
        >
          <FormattedMessage id="navigation.pendingChanges.modal.deployButton" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PendingChangesModal
